import React, {useState} from "react";
import ReactDOM from "react-dom/client";
import "react-phone-number-input/style.css";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import OutlinedInput from "@mui/material/OutlinedInput";
import PhoneInput from "react-phone-number-input";
import Spa from "@components/spas/Spa";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import styled from "styled-components";

const StyledPhoneInput = styled(PhoneInput)`
	input {
		padding: 13px 14px;
	}

	.PhoneInputInput {
		background-color: inherit;
		border: 1px solid rgba(0, 0, 0, 0.23);
		border-radius: 4px;
		
		&:hover {
			border-color: rgba(0, 0, 0, 0.87);
		}
		
		&:focus {
			border-color: rgb(0, 147, 239);
			outline: 1px solid rgb(0, 147, 239) !important;
		}
	}
`;

const Webform2FaModal = (props) => {

	const [message, setMessage] = useState('');
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [password, setPassword] = useState("");
	const [passwordShowInitialMessage, setPasswordShowInitialMessage] = useState(true);
	const [recipient, setRecipient] = useState("");
	const [recipientVerified, setRecipientVerified] = useState(false);
	const [contactMethod, setContactMethod] = useState("");
	const [phone, setPhone] = useState("");
	const [email, setEmail] = useState("");
	const [codeValue, setCodeValue] = useState("");

	const companyId = props.companyId;
	const formId = props.formId;
	const submittedFormId = props.submittedFormId;
	const verifier = props.verifier;
	const accessType = props.accessType;

	const handlePasswordChange = (e) => {
		setPassword(e.target.value);
	};

	const handleClickShowPassword = () => setShowPassword((show) => !show);
	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};

	const handleContactMethodChange = (e) => {
		setContactMethod(e.target.value);
		setRecipient("");
		setPhone("");
		setEmail("");
	};

	const handlePhoneChange = (value) => {
		setPhone(value);
		setRecipient(value);
		setEmail("");
	};

	const handleEmailChange = (e) => {
		setEmail(e.target.value);
		setRecipient(e.target.value);
		setPhone("");
	};

	const handleCodeValue = (e) => {
		setCodeValue(e.target.value);
	};

	const showErrorMessage = (message) => {
		setMessage(message);
	};

	const finaliseWithToken = (token) => {
		setIsAuthenticated(true);
		setMessage('');

		// set a token parameter using URLSearchParams to URL and redirect
		let url;
		url = new URL(window.location.href);
		url.searchParams.set("token", token);

		setTimeout(() => {
			window.location.href = url.href;
		}, 1000);
	};

	const handlePasswordVerifier = (event) => {
		event.preventDefault();

		// Prepare data object with state values
		const data = {
			company: companyId,
			id: formId,
			sfId: submittedFormId,
			verifier: verifier,
			secret: password,
		};
		// console.log("Data to be sent to server:", data);

		fetch("/", {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
			},
			body: new URLSearchParams({
				global: "true",
				type: "form",
				post: "access",
				_ajax: "true",
				...data
			}),
		})
		.then((response) =>{ return response.json(); })
		.then((data) => {
			// Handle the response from the PHP script
			// console.log("Response from server:", data);

			if (data.token ?? null) {
				return finaliseWithToken(data.token);

			} else {
				setPasswordShowInitialMessage(false);
				return showErrorMessage(data.error ?? 'An error occurred. Please try again.');
			}
		})
		.catch((error) => {
			// Handle any errors that occurred during the fetch
			setMessage('An error occurred. Please try again.');
			console.error("Error:", error);
		});
	};

	const handleSendSMSEmail = (event) => {
		event.preventDefault();

		const data = {
			company: companyId,
			id: formId,
			sfId: submittedFormId,
			verifier: verifier,
			contactMethod: contactMethod,
			recipient: recipient,
		};
		// check if email or phone is valid
		fetch("/", {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
			},
			body: new URLSearchParams({
				global: "true",
				type: "form",
				post: "access",
				_ajax: "true",
				...data
			}),
		})
			.then((response) =>{ return response.json(); })
			.then((data) => {
				// Handle the response from the PHP script
				// console.log("Response from server:", data);

				if (data.success ?? null) {
					// recipient verified, now need to enter code sent
					setRecipientVerified(true);
					setMessage('');
				} else {
					return showErrorMessage(data.error ?? 'An error occurred. Please try again.');
				}
			})
			.catch((error) => {
				// Handle any errors that occurred during the fetch
				setMessage('An error occurred. Please try again.');
				console.error("Error:", error);
			});
	};

	const handleProcessCode = (event) => {
		event.preventDefault();

		const data = {
			company: companyId,
			id: formId,
			sfId: submittedFormId,
			verifier: verifier,
			contactMethod: contactMethod,
			recipient: recipient,
			code: codeValue,
		};
		// send code to server to verify
		fetch("/", {
			method: "POST",
			headers: {
				"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
			},
			body: new URLSearchParams({
				global: "true",
				type: "form",
				post: "access",
				_ajax: "true",
				...data
			}),
		})
			.then((response) => { return response.json(); })
			.then((data) => {
				// Handle the response from the PHP script
				// console.log("Response from server:", data);

				if (data.success && (data.token ?? null)) {
					return finaliseWithToken(data.token);

				} else {
					return showErrorMessage(data.error ?? 'An error occurred. Please try again.');
				}
			})
			.catch((error) => {
				// Handle any errors that occurred during the fetch
				setMessage('An error occurred. Please try again.');
				console.error("Error:", error);
			});
	};

	let classNames = "shadow-lg m-auto relative p-20 justify-center flex flex-col items-center";

	return (
		<Container maxWidth="sm">
			<Box className={classNames}>
				<div className="text-blue-600 bg-blue-200 rounded-full p-2 flex flex-col items-center">
					<LockOutlinedIcon className="text-8xl w-24 h-24" />
				</div>
				<h4 className="mb-4">Authentication required</h4>
				{/*<p>{accessType}</p>*/}

				{message && (
					<Alert severity="error" className="mb-6">{message}</Alert>
				)}

				{isAuthenticated && (
					<Box className="flex flex-col items-center justify-center">
						<h5>You are verified.</h5>
						<p>Please wait while we redirect you to the form.</p>
						<CircularProgress className="mt-3" />
					</Box>
				)}

				{!isAuthenticated && accessType === "Password" && (
					<form autoComplete="off" onSubmit={handlePasswordVerifier} className="flex flex-col items-center">
						{passwordShowInitialMessage === true && (
							<Box sx={{ mb: 4, maxWidth: 300, textAlign: "center"}}>
								<p className="mb-0">
									This form requires verification.
									<br />
									Please enter the password you were given to
									gain access:
								</p>
							</Box>
						)}

						<FormControl
							sx={{ maxWidth: 320 }}
							variant="outlined"
							className="flex justify-center"
							required={true}
						>
							<InputLabel htmlFor="outlined-adornment-password">
								Password
							</InputLabel>
							<OutlinedInput
								id="outlined-adornment-password"
								type={showPassword ? "text" : "password"}
								onChange={handlePasswordChange}
								endAdornment={
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={handleClickShowPassword}
											onMouseDown={
												handleMouseDownPassword
											}
											edge="end"
										>
											{showPassword ? (
												<VisibilityOff />
											) : (
												<Visibility />
											)}
										</IconButton>
									</InputAdornment>
								}
								label="Password"
								inputProps = {{
									autoComplete: "off",
									'data-1p-ignore': true
								}}
							/>

							<Button
								type="submit"
								variant="contained"
								sx={{ maxWidth: 360, margin: "auto", mt: 3, }}
							>
								Authenticate
							</Button>
						</FormControl>
					</form>
				)}

				{/* "This form requires verification..."*/}
				{!isAuthenticated && accessType === "SMSOrEmail" && !recipientVerified && (
					<form autoComplete="off" onSubmit={handleSendSMSEmail} className="flex flex-col items-center">
						<Stack
							sx={{ width: "100%", maxWidth: 360, textAlign: "center" }}
							spacing={2}
							direction="row"
						>
							<p>
								This form requires verification. A code will
								be sent to your phone or email to verify
								your access:
							</p>
						</Stack>

						<Box className="p-4 flex flex-col items-center">
							<InputLabel sx={{ fontWeight: 600 }}>Verification Method</InputLabel>
							<ToggleButtonGroup
								color="primary"
								value={contactMethod}
								exclusive
								onChange={handleContactMethodChange}
								aria-label="Verification Method"
								variant="contained"
							>
								<ToggleButton value="sms">SMS</ToggleButton>
								<ToggleButton value="email">Email</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						{contactMethod === "sms" && (
							<FormControl
								sx={{ mt: 2, maxWidth: 460 }}
								variant="outlined"
								className="flex flex-col justify-center"
							>
								<StyledPhoneInput
									defaultCountry={"AU"}
									value={phone}
									className={`w-full`}
									placeholder={"0401 000 111"}
									required
									name={"phone"}
									id={"phone"}
									onChange={(v) => handlePhoneChange(v)}
									data-1p-ignore={true}
								/>
							</FormControl>
						)}

						{contactMethod === "email" && (
							<FormControl
								sx={{ mt: 2, maxWidth: 460, bg: "#fff" }}
								variant="outlined"
								className="flex flex-col justify-center"
							>
								<InputLabel htmlFor="outlined-adornment-email">
									Email Address
								</InputLabel>

								<OutlinedInput
									id="outlined-adornment-email"
									type="text"
									value={email}
									onChange={handleEmailChange}
									label="Email Address"
									required={true}
									inputProps={{ "data-1p-ignore": true }}
								/>
							</FormControl>
						)}

						{(contactMethod === "sms" || contactMethod === 'email') && (
							<Button
								type="submit"
								variant="contained"
								sx={{ mt: 3, mx: "auto", maxWidth: 360 }}
							>
								Get Code
							</Button>
						)}
					</form>
				)}

				{/* "A 6-digit code..."*/}
				{!isAuthenticated && recipientVerified && (
					<form autoComplete="off" onSubmit={handleProcessCode} className="flex flex-col items-center">
						<Stack
							sx={{ width: "100%", maxWidth: 360, textAlign: "center"}}
							spacing={2}
							direction="row"
						>
							<p>
								A 6-digit code has been sent to your phone or
								email. Please enter the code below:
							</p>
						</Stack>

						<FormControl
							sx={{ mt: 3, maxWidth: 460 }}
							variant="outlined"
							className="flex flex-col justify-center"
						>
							<InputLabel htmlFor="outlined-adornment-password">
								Enter Code
							</InputLabel>

							<OutlinedInput
								id="outlined-adornment-password"
								type="text"
								onChange={handleCodeValue}
								label="Enter Code"
								inputProps={{ "data-1p-ignore": true }}
							/>

							<Button
								type="submit"
								variant="contained"
								sx={{ mt: 3, mx: "auto", maxWidth: 360 }}
							>
								Submit
							</Button>
						</FormControl>
					</form>
				)}
			</Box>
		</Container>
	);
};

const Webform2FaModalWrapper = (props) => (
	<Spa>
		<Webform2FaModal {...props} />
	</Spa>
);

document.addEventListener("DOMContentLoaded", function () {
	document.querySelectorAll(".react-2fa-modal").forEach((domContainer) => {
		const root = ReactDOM.createRoot(domContainer);
		root.render(React.createElement(Webform2FaModalWrapper, domContainer.dataset));
	});
});

export default Webform2FaModalWrapper;